import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getDisableCheckoutContinueButtonForPickUp, getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";

/**
 * The address option the shopper currently has selected on the CheckoutShipping page.
 * Derived entirely from persisted Redux/cart state so it survives page remounts.
 */
export type SelectedShippingAddressType = "" | "saved" | "billing" | "onetime";

/**
 * Single source of truth for which shipping address option is selected.
 * "onetime"  -> a new one-time address is being entered
 * "billing"  -> the billing address is used as the shipping address
 * "saved"    -> a saved ship-to address is selected on the cart
 * ""         -> nothing selected yet
 *
 * Derived from the cart + loaded ship-to, plus the CheckoutShipping page flag for one-time addresses
 * (since one-time entries aren’t persisted to cart.shipToId until the shopper continues), so it is correct
 * immediately on refresh, before/without SetInitialValues re-hydrating the flags.
 */
export const getSelectedShippingAddressType = (
    state: CustomApplicationState,
): SelectedShippingAddressType => {
    const { cartId, useOneTimeAddress } = state.pages.checkoutShipping;
    // No option is preselected on page entry (refresh or nav from cart); the shopper must pick one explicitly.
    if (!state.SURData.shippingAddressOptionSelected.isSelected) {
        return "";
    }
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    if (!cart) {
        return "";
    }
    // One-time addresses live in the form state and are not persisted to cart.shipToId
    // until continue, so detect them from the page flag before the shipToId guard.
    if (useOneTimeAddress) {
        return "onetime";
    }
    if (!cart.shipToId) {
        return "";
    }
    const shipTo = getShipToState(state, cart.shipToId).value;
    if (shipTo?.oneTimeAddress) {
        return "onetime";
    }
    if (cart.billToId && cart.shipToId === cart.billToId) {
        return "billing";
    }
    return shipTo ? "saved" : "";
};

/** True when the shopper has any shipping address selected (saved, billing, or one-time). */
export const hasSelectedShippingAddress = (state: CustomApplicationState): boolean =>
    getSelectedShippingAddressType(state) !== "";

/**
 * Derived validity flags for the CheckoutShipping "Continue"/"Next" buttons.
 * Centralized so the CartTotal Continue button, its inline notes, and the page
 * container Next button all disable under the exact same conditions.
 */
export interface CheckoutShippingContinueState {
    isLoading: boolean;
    /** The selected address residential flag ("1" = business), or undefined when unknown. */
    isBusinessAddress: string | undefined;
    businessAddressWithOutDockDoor: boolean;
    /** True when the shopper has a shipping address selected (replaces the old SURData flag). */
    hasSelectedAddress: boolean;
    notUS: boolean;
    shouldDisableForCountry: boolean;
    isFormValid: boolean;
    isValidPostal: boolean;
    isContinueButtonDisabled: boolean;
}

/**
 * Single source of truth for CheckoutShipping continue/next button validity.
 */
export const getCheckoutShippingContinueState = (
    state: CustomApplicationState,
): CheckoutShippingContinueState => {
    const { cartId, isUpdatingCart } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    const isLoading = !cartState.value || cartState.isLoading || isUpdatingCart;
    const fulfillmentMethod = state.context.session.fulfillmentMethod;
    const useOneTimeAddress = state.pages.checkoutShipping.useOneTimeAddress;
    const formState = state.pages.checkoutShipping.shippingAddressFormState;
    const shipToState = getShipToState(state, cartState.value ? cartState.value.shipToId : undefined).value;
    const isUkWebsite = getIsUkWebsite(state);
    const disableCheckoutContinueButtonForPickUp = getDisableCheckoutContinueButtonForPickUp(state);
    // Country comes from the address source actually in use (one-time form vs saved ship-to).
    const selectedCountryAbbreviation = useOneTimeAddress
        ? formState?.address?.country?.abbreviation
        : shipToState?.country?.abbreviation;
    // Pickup orders never need a shipping address selection, so treat them as satisfied.
    const hasSelectedAddress =
        fulfillmentMethod === FulfillmentMethod.PickUp || hasSelectedShippingAddress(state);
    const useBillingAddress = state.pages.checkoutShipping.useBillingAddress;

    const isBusinessAddress = useOneTimeAddress
        ? formState?.address.properties.residentialFlag
        : shipToState?.properties.residentialFlag;
    const isDockDoorAddress = useOneTimeAddress
        ? formState?.address.properties.dockDoorRequired
        : shipToState?.properties.dockDoorRequired;
    const businessAddressWithOutDockDoor =
        isBusinessAddress === "1" && !isDockDoorAddress && fulfillmentMethod === FulfillmentMethod.Ship;

    const notUS =
        fulfillmentMethod !== FulfillmentMethod.PickUp && selectedCountryAbbreviation !== "US";

    // UK storefront ships only to GB/IE; the selected-address country decides (sandbox UK division).
    const notUK =
        fulfillmentMethod !== FulfillmentMethod.PickUp &&
        selectedCountryAbbreviation !== "GB" &&
        selectedCountryAbbreviation !== "IE";
    const shouldDisableForCountry =
        fulfillmentMethod === FulfillmentMethod.Ship && (isUkWebsite ? notUK : notUS);

    const stateRequired = !isUkWebsite;

    const isFormValid =
        fulfillmentMethod === FulfillmentMethod.PickUp ||
        (!!formState?.address?.address1?.trim() &&
            !!formState?.address?.city?.trim() &&
            (!stateRequired || !!formState?.address?.state));

    const isValidPostal =
        isUkWebsite ||
        shipToState?.postalCode.length === 5 ||
        (shipToState?.postalCode.length === 10 && shipToState?.postalCode.includes("-"));

    const isContinueButtonDisabled =
        (disableCheckoutContinueButtonForPickUp && fulfillmentMethod === FulfillmentMethod.PickUp) ||
        shouldDisableForCountry ||
        ((isLoading || !isBusinessAddress || businessAddressWithOutDockDoor) &&
            fulfillmentMethod === FulfillmentMethod.Ship) ||
        !hasSelectedAddress ||
        (useBillingAddress && !isValidPostal && fulfillmentMethod === FulfillmentMethod.Ship) ||
        !isFormValid;

    return {
        isLoading,
        isBusinessAddress,
        businessAddressWithOutDockDoor,
        hasSelectedAddress,
        notUS,
        shouldDisableForCountry,
        isFormValid,
        isValidPostal,
        isContinueButtonDisabled,
    };
};
